import React, { useEffect, useState } from 'react';
import { mediaUrl } from '../utils/mediaUrl';
import { useLocation } from 'react-router-dom';
import { getOrderByOrderId } from '../services/orderService';
import '../css/product.css';
import '../css/phase1.css';

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

// Public order tracking. Asks for the order ID and the last 4 digits of the
// delivery phone number so strangers can't look up other people's orders.
const TrackOrderPage = () => {
  const location = useLocation();
  const [orderId, setOrderId] = useState('');
  const [phone, setPhone] = useState('');
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const q = new URLSearchParams(location.search).get('orderId');
    if (q) setOrderId(q);
  }, [location.search]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const id = orderId.trim().toUpperCase();
    if (!id || phone.length !== 4) {
      setError('Enter your order ID and the last 4 digits of the phone number used for delivery.');
      return;
    }
    setLoading(true);
    setError('');
    setTracking(null);
    try {
      setTracking(await getOrderByOrderId(id, phone));
    } catch (err) {
      setError(err.response?.data?.message || 'No order found with that order ID and phone number');
    } finally {
      setLoading(false);
    }
  };

  const shipment = tracking?.shipment;

  return (
    <div className="track-page">
      <div className="track-container">
        <h1>Track your order</h1>
        <p className="subtitle">You'll find your order ID in your confirmation email and under My Orders.</p>

        <form className="p1-track-form" onSubmit={onSubmit}>
          <input
            type="text"
            aria-label="Order ID"
            placeholder="Order ID, e.g. ORD-261005-K7Q2XM"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
          />
          <input
            type="text"
            inputMode="numeric"
            aria-label="Last 4 digits of phone number"
            placeholder="Last 4 digits of phone"
            maxLength={4}
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
          />
          <button type="submit" className="p1-btn" disabled={loading}>
            {loading ? 'Finding…' : 'Track'}
          </button>
        </form>

        {error && <p className="error">{error}</p>}

        {tracking && (
          <div className="tracking-wrapper">
            <div className="card order-summary">
              <h2>Order {tracking.orderId}</h2>
              <p>
                <strong>Status:</strong>{' '}
                <span className={`status-badge ${tracking.status.toLowerCase().replace(/\s+/g, '-')}`}>
                  {tracking.status}
                </span>
              </p>
              <p><strong>Placed on:</strong> {formatDate(tracking.createdAt)}</p>
              {tracking.isDelivered && tracking.deliveredAt ? (
                <p><strong>Delivered on:</strong> {formatDate(tracking.deliveredAt)}</p>
              ) : (
                shipment?.etd && <p><strong>Expected delivery:</strong> {shipment.etd}</p>
              )}
              <p>
                <strong>Payment:</strong>{' '}
                {tracking.paymentMethod === 'COD'
                  ? tracking.isPaid ? 'Cash on Delivery (paid)' : `Cash on Delivery — ₹${tracking.totalPrice} to pay`
                  : 'Paid online'}
              </p>
              <p>
                <strong>Delivering to:</strong> {tracking.shippingAddress?.city}, {tracking.shippingAddress?.state}{' '}
                {tracking.shippingAddress?.pincode}
              </p>
              {tracking.returnStatus && tracking.returnStatus !== 'none' && (
                <p><strong>Return:</strong> {tracking.returnStatus.replace('_', ' ')}</p>
              )}
            </div>

            {shipment && (
              <div className="card">
                <h2>Shipment</h2>
                <p>
                  {shipment.courierName} · Tracking no. <strong>{shipment.awbCode}</strong>
                </p>
                {shipment.trackingUrl && (
                  <p>
                    <a href={shipment.trackingUrl} target="_blank" rel="noreferrer">Track on courier site</a>
                  </p>
                )}
                {shipment.activities?.length > 0 && (
                  <ul className="p1-activity">
                    {shipment.activities.map((a, i) => (
                      <li key={i}>
                        {a.activity || a.status}
                        {a.location ? ` — ${a.location}` : ''}
                        {a.date && <time>{a.date}</time>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="card item-card">
              <h2>Items</h2>
              {tracking.orderItems.map((item, i) => (
                <div key={i} className="item-row">
                  {item.image && <img src={mediaUrl(item.image)} alt={item.name} />}
                  <div>
                    <h4>{item.name}</h4>
                    <p>Qty: {item.qty}{item.size ? ` · Size ${item.size}` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrderPage;
